window.lcModalFocus = (() => {
  let releaseCurrent = null;
  return (root, panel, initialFocus, opener, close) => {
    releaseCurrent?.(false);
    const previousInert = new Map();
    const selector = 'a[href], button, input, select, textarea, summary, [tabindex], [contenteditable="true"]';
    let released = false;
    let closing = false;
    let lastFocus = initialFocus || panel;
    const controls = () => [...panel.querySelectorAll(selector)].filter(element =>
      element.tabIndex >= 0 && !element.disabled && !element.closest('[inert], [hidden]') &&
      element.getClientRects().length && getComputedStyle(element).visibility === 'visible'
    );
    const makeInert = element => {
      if (!(element instanceof HTMLElement) || element === root || element.contains(root) ||
          /^(SCRIPT|STYLE|LINK)$/.test(element.tagName) || previousInert.has(element)) return;
      previousInert.set(element, element.inert);
      element.inert = true;
    };
    const observer = new MutationObserver(records => records.forEach(record =>
      record.addedNodes.forEach(makeInert)
    ));
    // Lichcards 13-10-2026: de rest van de pagina wordt pas 'inert' na de eerste paint van de lade. Inert zetten laat de
    // browser de stijl van de hele pagina herberekenen; vóór de paint maakte dat het openen ±0,2 s trager. Toetsenbord-
    // focus blijft intussen in de lade via de Tab- en focusin-afhandeling hieronder.
    let inertFrame = 0;
    let inertTimer = 0;
    const applyInert = () => {
      inertFrame = 0;
      inertTimer = 0;
      if (released) return;
      let branch = root;
      while (branch.parentElement) {
        [...branch.parentElement.children].filter(element => element !== branch).forEach(makeInert);
        if (branch.parentElement === document.body) break;
        branch = branch.parentElement;
      }
      observer.observe(document.body, { childList: true });
    };
    inertFrame = requestAnimationFrame(() => { inertFrame = 0; inertTimer = setTimeout(applyInert, 0); });
    const onKeyDown = event => {
      if (root.isComposingSearchInputEvent?.(event)) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        const localizationPicker = event.target.closest?.('lc-localization-picker');
        const localizationDetails = localizationPicker?.querySelector('details[open]');
        if (localizationDetails && panel.contains(localizationPicker)) {
          localizationDetails.open = false;
          localizationPicker.querySelector('summary')?.focus({ preventScroll: true });
          return;
        }
        if (!closing) { closing = true; close(); }
      } else if (event.key === 'Tab') {
        const items = controls();
        const current = items.indexOf(document.activeElement);
        const next = event.shiftKey ? (current <= 0 ? items.length - 1 : current - 1) : (current + 1) % items.length;
        event.preventDefault();
        event.stopImmediatePropagation();
        (items[next] || panel).focus();
      }
    };
    const onKeyUp = event => {
      if (root.isComposingSearchInputEvent?.(event)) return;
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    const onFocus = event => {
      if (panel.contains(event.target)) { lastFocus = event.target; return; }
      const items = controls();
      (items.includes(lastFocus) ? lastFocus : items[0] || panel).focus({ preventScroll: true });
    };
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('keyup', onKeyUp, true);
    document.addEventListener('focusin', onFocus, true);
    (initialFocus || controls()[0] || panel).focus({ preventScroll: true });
    const release = (restoreFocus = true) => {
      if (released) return;
      released = true;
      if (inertFrame) cancelAnimationFrame(inertFrame);
      clearTimeout(inertTimer);
      observer.disconnect();
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('keyup', onKeyUp, true);
      document.removeEventListener('focusin', onFocus, true);
      previousInert.forEach((value, element) => { element.inert = value; });
      if (releaseCurrent === release) releaseCurrent = null;
      if (restoreFocus && (root.id === 'Drawer-Menu' || root.id === 'Drawer-Search')) {
        const selectors = root.id === 'Drawer-Search'
          ? ['#header-component .header__search--inline input[type="search"]', '#header-component side-drawer-opener[data-side-drawer="#Drawer-Search"] button', '#header-component .header__heading-link']
          : ['#header-component #HeaderMenu-tcg', '#header-component side-drawer-opener[data-side-drawer="#Drawer-Menu"] button', '#header-component .header__heading-link'];
        [opener, ...selectors.map(selector => document.querySelector(selector))].some(target => {
          if (!target?.isConnected || target.closest('[inert], [hidden]') || !target.getClientRects().length || getComputedStyle(target).visibility !== 'visible') return false;
          target.focus({ preventScroll: true });
          return document.activeElement === target;
        });
      } else if (restoreFocus && opener?.isConnected && !opener.closest('[inert]')) opener.focus({ preventScroll: true });
    };
    releaseCurrent = release;
    return release;
  };
})();

class SideDrawerOpener extends HTMLElement {
  constructor() {
    super();
    const button = this.querySelector('button');
    if (!button) return;

    button.addEventListener('click', () => {
      const drawer = document.querySelector(this.getAttribute('data-side-drawer'));

      if(button.closest('.header__icon--menu')) {
        if(button.classList.contains('active')) {
          button.classList.remove('active');
          if (drawer) drawer.close();
        } else {
          // Lichcards 13-10-2026: hier werd --header-height op <html> gezet. <body> zet die variabele zelf al (theme.liquid,
          // header.js), dus de waarde op <html> werd nergens gebruikt, maar liet bij het openen van het menu de stijl van de
          // hele pagina opnieuw berekenen (±0,25 s op een trage telefoon). Daarom weggelaten.
          button.classList.add('active');
          if (drawer) drawer.open(button);
        }
      } else {
        if (drawer) drawer.open(button);
      }
    });
  }
}
if (!customElements.get('side-drawer-opener')) customElements.define('side-drawer-opener', SideDrawerOpener);

class SideDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => {
      if (this.isComposingSearchInputEvent(evt)) return;
      if (evt.code === 'Escape') this.close();
    });
    this.overlay = this.querySelector('[id^="Drawer-Overlay-"]');
    this.overlay?.addEventListener('click', this.close.bind(this));
  }

  isComposingSearchInputEvent(event) {
    return this.id === 'Drawer-Search' &&
      (event.isComposing === true || event.keyCode === 229) &&
      event.target === this.querySelector('input[type="search"]');
  }

  get isTabletViewport() {
    try {
      return window.matchMedia('(max-width: 1024px)').matches;
    } catch (e) {
      return window.innerWidth <= 1024;
    }
  }

  get usesModalFocus() {
    return this.id === 'Drawer-Menu' || this.id === 'Drawer-Search';
  }

  get reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  connectedCallback() {
    if (!this.dataset.moved) {
      this.dataset.moved = true;
      document.body.appendChild(this);
    }
  }

  disconnectedCallback() {
    if (this.isConnected) return;
    const wasOpen = this.classList.contains('open');
    this.beginDrawerTransition();
    this.lcClosing = true;
    this.removeAttribute('data-lc-closing');
    this.releaseFocus(!this.hasOtherOpenModal());
    this.classList.remove('active', 'open');
    if (wasOpen) this.releaseScrollLock();
  }

  beginDrawerTransition() {
    this.drawerTransitionGeneration = (this.drawerTransitionGeneration || 0) + 1;
    clearTimeout(this.openTimer);
    this.openTimer = null;
    this.removeDrawerTransitionListeners();
    this.drawerAnimation?.stop?.();
    this.drawerAnimation = null;
    this.querySelector('[data-drawer-content]')?.getAnimations?.().forEach(animation => animation.cancel());
    return this.drawerTransitionGeneration;
  }

  removeDrawerTransitionListeners() {
    if (this.drawerOpenedHandler) this.removeEventListener('transitionend', this.drawerOpenedHandler);
    if (this.drawerTransitionStartHandler) this.removeEventListener('transitionstart', this.drawerTransitionStartHandler);
    if (this.drawerTransitionEndHandler) this.removeEventListener('transitionend', this.drawerTransitionEndHandler);
    this.drawerOpenedHandler = null;
    this.drawerTransitionStartHandler = null;
    this.drawerTransitionEndHandler = null;
  }

  hasOtherOpenModal() {
    return Array.from(document.querySelectorAll('side-drawer.open, cart-drawer.open, pickup-availability-drawer.open, modal-dialog[open], product-modal[open], quick-add-modal[open], quick-view-modal[open], bulk-modal[open], modal-component[open], dialog[open]'))
      .some(element => element !== this && element.isConnected && !element.lcClosing);
  }

  releaseScrollLock() {
    if (this.hasOtherOpenModal()) return;
    document.body.classList.remove('overflow-hidden');
    document.documentElement.removeAttribute('scroll-lock');
  }

  getReturnFocusTarget() {
    const opener = this.activeElement;
    if (this.id !== 'Global-Drawer' || !(opener instanceof HTMLElement) || opener.getClientRects().length) return opener;
    if (!opener.closest('side-drawer-opener.toggle-title[data-side-drawer="#Global-Drawer"][data-view-content="sidebar"]')) return opener;
    const panel = opener.closest('.tab-content[id]');
    const section = panel?.closest('.section-product-tabs');
    const target = section && Array.from(section.querySelectorAll('.tabs-list .tab-title')).find(tab => tab.getAttribute('href') === `#${panel.id}`);
    if (!target?.isConnected || target.closest('[hidden], [inert]') || !target.getClientRects().length || getComputedStyle(target).visibility !== 'visible') return opener;
    return target;
  }

  releaseFocus(restoreFocus) {
    const release = this.lcReleaseFocus;
    this.lcReleaseFocus = null;
    release?.(restoreFocus);
    if (this.drawerFocusHandler && typeof trapFocusHandlers !== 'undefined' &&
        this.drawerFocusHandler === trapFocusHandlers.keydown) {
      removeTrapFocus(restoreFocus ? this.getReturnFocusTarget() : undefined);
    }
    this.drawerFocusHandler = null;
  }

  drawerAnimationState(opening) {
    const config = typeof theme !== 'undefined' ? theme.config || {} : {};
    let direction = this.getAttribute('data-drawer-direction');
    const mobileDirection = this.getAttribute('data-mobile-drawer-direction');
    if (this.isTabletViewport && mobileDirection) direction = mobileDirection;
    if (config.rtl && (direction === 'left' || direction === 'right')) {
      direction = direction === 'left' ? 'right' : 'left';
    }
    const closed = direction === 'center' ? 'translate(-50%, -40%)' :
      direction === 'left' ? 'translateX(-100%)' :
      direction === 'right' ? 'translateX(100%)' : 'translateX(0)';
    const opened = direction === 'center' ? 'translate(-50%, -50%)' : 'translateX(0)';
    return {
      content: this.querySelector('[data-drawer-content]'),
      opacity: opening ? [0, 1] : [1, 0],
      transform: opening ? [closed, opened] : [opened, closed],
      duration: opening || direction === 'center' ? 0.6 : 0.3,
      easing: config.easing || [0.61, 0.22, 0.23, 1],
    };
  }

  animateDrawer(state) {
    if (!state.content) return Promise.resolve();
    if (this.reducedMotion) {
      state.content.style.opacity = String(state.opacity[1]);
      state.content.style.transform = state.transform[1];
      return Promise.resolve();
    }
    const animation = Motion.timeline([
      [state.content, { opacity: state.opacity, transform: state.transform },
        { duration: state.duration, easing: state.easing }],
    ]);
    this.drawerAnimation = animation;
    return animation.finished.catch(() => {});
  }

  open(triggeredBy) {
    const generation = this.beginDrawerTransition();
    this.lcClosing = false;
    this.removeAttribute('data-lc-closing');
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.classList.add('open');
    document.body.classList.add('overflow-hidden');
    document.documentElement.setAttribute('scroll-lock', '');

    const activate = () => {
      if (generation !== this.drawerTransitionGeneration || !this.isConnected || this.lcClosing) return;
      this.classList.add('animate', 'active');
      if (this.usesModalFocus) this.trapFocus();
    };
    if (this.reducedMotion) activate();
    else this.openTimer = setTimeout(activate);

    let openedHandled = false;
    const onOpened = event => {
      const content = this.querySelector('[data-drawer-content]');
      if (event && event.target !== this && event.target !== content) return;
      if (openedHandled || generation !== this.drawerTransitionGeneration || !this.isConnected ||
          this.lcClosing || !this.classList.contains('open')) return;
      openedHandled = true;
      this.removeEventListener('transitionend', onOpened);
      this.drawerOpenedHandler = null;
      if (!this.classList.contains('active')) activate();
      if (!this.usesModalFocus) this.trapFocus();
    };
    this.drawerOpenedHandler = onOpened;
    this.addEventListener('transitionend', onOpened);
    this.handleTransition(true, 'drawer--opening', 'drawer--open', generation);
    this.animateDrawer(this.drawerAnimationState(true)).then(() => {
      onOpened();
      this.finishDrawerTransition(generation, true);
    });
  }

  async close() {
    if (this.lcClosing || !this.classList.contains('open')) return;
    const generation = this.beginDrawerTransition();
    this.lcClosing = true;
    this.setAttribute('data-lc-closing', '');
    this.releaseFocus(!this.hasOtherOpenModal());
    this.releaseScrollLock();
    this.handleTransition(false, 'drawer--closing', '', generation);

    if (this.id === 'Drawer-Menu') {
      document.querySelector('.header__icon--menu button.active')?.classList.remove('active');
    }
    const detailsElement = this.overlay?.closest('details');
    await this.animateDrawer(this.drawerAnimationState(false));
    if (generation !== this.drawerTransitionGeneration || !this.isConnected || !this.lcClosing) return;
    this.classList.remove('active', 'open');
    this.removeAttribute('data-lc-closing');
    this.finishDrawerTransition(generation, false);
    this.releaseScrollLock();
    if (detailsElement) {
      detailsElement.removeAttribute('open');
      detailsElement.classList.remove('menu-opening');
      if (!this.hasOtherOpenModal()) document.body.classList.remove('overflow-hidden-mobile');
    }
  }

  finishDrawerTransition(generation, opening) {
    if (generation !== this.drawerTransitionGeneration || !this.isConnected) return;
    this.removeDrawerTransitionListeners();
    if (this.hasOtherOpenModal()) return;
    document.body.classList.remove('drawer--closing', 'drawer--opening', 'drawer--open');
    if (opening && this.classList.contains('open') && !this.lcClosing) document.body.classList.add('drawer--open');
  }

  handleTransition(checkOpen, startClass, endClass = '', generation = this.drawerTransitionGeneration) {
    if (!this.querySelector('.side-drawer:not(.popup__inner)')) return;
    const content = this.querySelector('[data-drawer-content]');
    const isCurrent = event => generation === this.drawerTransitionGeneration && this.isConnected &&
      !this.hasOtherOpenModal() && (!event || event.target === this || event.target === content);
    const start = event => {
      if (!isCurrent(event)) return;
      document.body.classList.remove('drawer--open', 'drawer--opening', 'drawer--closing');
      document.body.classList.add(startClass);
    };
    const end = event => {
      if (!isCurrent(event)) return;
      this.removeEventListener('transitionstart', start);
      this.removeEventListener('transitionend', end);
      this.drawerTransitionStartHandler = null;
      this.drawerTransitionEndHandler = null;
      document.body.classList.remove('drawer--open', 'drawer--opening', 'drawer--closing');
      if (endClass && checkOpen && !this.lcClosing) document.body.classList.add(endClass);
    };
    this.drawerTransitionStartHandler = start;
    this.drawerTransitionEndHandler = end;
    this.addEventListener('transitionstart', start);
    this.addEventListener('transitionend', end);
    if (!this.reducedMotion) start();
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  trapFocus() {
    if (this.lcClosing || !this.isConnected || !this.classList.contains('active') ||
        (!this.usesModalFocus && this.closest('[inert]'))) return;
    const panel = this.querySelector('[role="dialog"], .drawer__inner, .popup__inner, .blog-posts__sidebar');
    if (!panel) return;
    const initialFocus = panel.querySelector('.search__input, .popup__input') || panel.querySelector('.drawer__close');
    if (this.usesModalFocus) {
      if (this.id === 'Drawer-Search') {
        const searchScroller = this.querySelector('.search-drawer__inner');
        if (searchScroller) searchScroller.scrollTop = 0;
      }
      this.lcReleaseFocus?.(false);
      removeTrapFocus();
      this.lcReleaseFocus = window.lcModalFocus(this, panel, initialFocus, this.activeElement, () => this.close());
      return;
    }
    trapFocus(panel, initialFocus || panel);
    if (typeof trapFocusHandlers !== 'undefined') this.drawerFocusHandler = trapFocusHandlers.keydown;
  }
}
if (!customElements.get('side-drawer')) customElements.define('side-drawer', SideDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('#Modal-Overlay')?.addEventListener('click', this.hide.bind(this));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    this.addEventListener('click', (event) => {
      if (event.target === this) this.hide();
    });
  }

  connectedCallback() {
    if (!this.dataset.moved) {
      this.dataset.moved = true;
      this.dataset.section = this.closest('.shopify-section')?.id.replace('shopify-section-', '');
      document.body.appendChild(this);
    }
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    document.documentElement.setAttribute('scroll-lock', '');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    this.trapFocus();
    // window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.documentElement.removeAttribute('scroll-lock');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }

  trapFocus() {
    if (!this.isConnected || !this.hasAttribute('open')) return;
    const containerToTrapFocusOn = this.querySelector('.drawer__inner, .popup__inner');
    const focusElement = this.querySelector('[role="dialog"], .popup__inner');
    if (containerToTrapFocusOn) trapFocus(containerToTrapFocusOn, focusElement || containerToTrapFocusOn);
  }
}
if (!customElements.get('modal-dialog')) customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();
    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
if (!customElements.get('modal-opener')) customElements.define('modal-opener', ModalOpener);

class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}
if (!customElements.get('details-disclosure')) customElements.define('details-disclosure', DetailsDisclosure);

class CollapsibleDetails extends HTMLDetailsElement {
  constructor() {
    super();
    this.onSummaryClick = this._toggleOpen.bind(this);
    this.onViewportResize = theme.utils.rafThrottle(this.resizeHandler.bind(this));
    this.onViewportUnmatch = this.resizeHandler.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.summary = this.querySelector('summary');
    this.content = this.summary?.nextElementSibling;
    if (!this.summary || !this.content) return;
    this._isOpen = this.hasAttribute('open');
    this.syncExpanded(this._isOpen);
    this.summary.removeEventListener('click', this.onSummaryClick);
    this.summary.addEventListener('click', this.onSummaryClick);

    if (this.hasAttribute('collapsible-mobile')) {
      this.mobileViewport = null;
      this.resizeHandler();
      window.addEventListener('resize', this.onViewportResize);
      document.addEventListener('unmatchSmall', this.onViewportUnmatch);
    }

    if (!this.designModeListenersAdded && Shopify.designMode && this.hasAttribute('check-shopify-design-mode')) {
      this.designModeListenersAdded = true;
      this.addEventListener('shopify:block:select', () => this.isOpen = true);
      this.addEventListener('shopify:block:deselect', () => this.isOpen = false);
    }
  }

  disconnectedCallback() {
    this.summary?.removeEventListener('click', this.onSummaryClick);
    window.removeEventListener('resize', this.onViewportResize);
    document.removeEventListener('unmatchSmall', this.onViewportUnmatch);
    this.cancelAnimation();
    this.applyFinalState(this._isOpen);
  }

  static get observedAttributes() {
    return ['open'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'open') this.syncExpanded(newValue !== null);
  }

  syncExpanded(open) {
    const value = open ? 'true' : 'false';
    this.setAttribute('aria-expanded', value);
    this.summary?.setAttribute('aria-expanded', value);
  }

  get isOpen() {
    return this._isOpen;
  }

  set isOpen(value) {
    value = Boolean(value);
    if (value !== this._isOpen) {
      this._isOpen = value;

      if (this.isConnected && this.summary && this.content) {
        this._animate(value);
      } else {
        this.applyFinalState(value);
      }
    }
    this.syncExpanded(value);
  }

  _toggleOpen(event) {
    event.preventDefault();
    this.isOpen = !this.isOpen;
  }

  close() {
    this.isOpen = false;
  }

  resizeHandler() {
    const mobileViewport = window.matchMedia('(max-width: 749px)').matches;
    if (mobileViewport === this.mobileViewport) return;
    this.mobileViewport = mobileViewport;
    this.cancelAnimation();
    this._isOpen = !mobileViewport;
    this.applyFinalState(this._isOpen);
  }

  cancelAnimation() {
    this.animationGeneration = (this.animationGeneration || 0) + 1;
    this.collapsibleAnimation?.stop?.();
    this.collapsibleAnimation = null;
    return this.animationGeneration;
  }

  applyFinalState(open) {
    open ? this.setAttribute('open', '') : this.removeAttribute('open');
    this.syncExpanded(open);
    this.style.height = 'auto';
    this.style.overflow = 'visible';
    if (this.content) {
      this.content.style.opacity = open ? '1' : '0';
      this.content.style.transform = 'translateX(0)';
    }
  }

  async _animate(open) {
    const generation = this.cancelAnimation();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.applyFinalState(open);
      return;
    }
    const height = this.getBoundingClientRect().height;
    this.style.overflow = 'hidden';
    if (open) {
      const wasOpen = this.hasAttribute('open');
      this.setAttribute('open', '');

      this.collapsibleAnimation = Motion.timeline([
        [this, { height: [`${wasOpen ? height : this.summary.clientHeight}px`, `${this.scrollHeight}px`] }, { duration: 0.45, easing: 'cubic-bezier(0.7, 0, 0.3, 1)' }],
        [this.content, { opacity: [0, 1], transform: ['translateX(-1rem)', 'translateX(0)'] }, { duration: 0.25, at: '-0.1' }]
      ]);
    } else {
      this.collapsibleAnimation = Motion.timeline([
        [this.content, { opacity: 0, transform: ['translateX(0)', 'translateX(1rem)'] }, { duration: 0.15 }],
        [this, { height: [`${height}px`, `${this.summary.clientHeight}px`] }, { duration: 0.35, at: '<', easing: 'cubic-bezier(0.7, 0, 0.3, 1)' }]
      ]);
    }
    await this.collapsibleAnimation.finished.catch(() => {});
    if (generation !== this.animationGeneration || !this.isConnected || this._isOpen !== open) return;
    this.collapsibleAnimation = null;
    this.applyFinalState(open);
  }
}
if (!customElements.get('collapsible-details')) customElements.define('collapsible-details', CollapsibleDetails, { extends: 'details' });

class DropdownDetails extends HTMLDetailsElement {
  constructor() {
    super();

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this));
    } else {
      theme.initWhenVisible(this.init.bind(this));
    }

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => this.isOpen = true);
      this.addEventListener('shopify:block:deselect', () => this.isOpen = false);
    }
  }

  connectedCallback() {
    if (this._initialized && this.isOpen) this._animate(true, true);
  }

  disconnectedCallback() {
    cancelAnimationFrame(this._hoverRaf);
    clearTimeout(this._hoverCloseTimer);
    document.removeEventListener('click', this.onClickOutsideListener);
    document.removeEventListener('keydown', this.onEscKeyboardListener);
    document.removeEventListener('focusout', this.onFocusOutListener);
  }

  init() {
    if (!this.isConnected) return;
    if (this._initialized) {
      if (this.isOpen) this._animate(true, true);
      return;
    }
    this._isOpen = this.hasAttribute('open') && this.getAttribute('open') !== 'false';
    this._suppressSummaryClick = false;
    this.elements = {
      button: this.querySelector('.details__summary'),
      dropdown: this.querySelector('.details__list')
    };

    if (!this.elements.button || !this.elements.dropdown) return;
    this._initialized = true;

    this._syncOpenAttributes(this._isOpen);
    this.elements.button.addEventListener('click', this._onSummaryClick.bind(this));
    this.elements.button.addEventListener('keydown', this._onSummaryKeydown.bind(this));

    this.onClickOutsideListener = this.onClickOutside.bind(this);
    this.onEscKeyboardListener = this.onEscKeyboard.bind(this);
    this.onFocusOutListener = this.onFocusOut.bind(this);
    if (this._isOpen) this._animate(true, true);

    if (theme.config.isTouch) return;
    if (theme.config.mqlDesktop && this.getAttribute('activate-event') === 'hover') {
      this.addEventListener('mouseenter', this.onHover.bind(this));
      this.addEventListener('mouseleave', this.onHover.bind(this));
    }
  }

  _syncOpenAttributes(open) {
    const summary = this.elements?.button;
    if (open) {
      this.setAttribute('open', 'true');
      summary?.setAttribute('aria-expanded', 'true');
    } else {
      this.removeAttribute('open');
      summary?.setAttribute('aria-expanded', 'false');
    }
  }

  /** @returns {string|null} href from header-menu-summary-link ([data-href] inside summary) */
  _getSummaryParentHref(summary = this.elements?.button) {
    const href = summary?.querySelector('[data-href]')?.dataset?.href;
    return href || null;
  }

  _onSummaryKeydown(event) {
    if (event.target.closest('a[href]')) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this._suppressSummaryClick = true;

    const href = this._getSummaryParentHref();
    if (href) {
      window.location.assign(href);
      return;
    }

    this.isOpen = !this.isOpen;
  }

  _onSummaryClick(event) {
    if (this._suppressSummaryClick) {
      this._suppressSummaryClick = false;
      if (event.detail === 0) {
        event.preventDefault();
        return;
      }
    }
    this._toggleOpen(event);
  }

  static get observedAttributes() {
    return ['open'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'open' && this.elements?.button) {
      this.elements.button.setAttribute('aria-expanded', String(newValue !== null && newValue !== 'false'));
    }
  }

  get isOpen() {
    return this._isOpen;
  }

  set isOpen(value) {
    if (value !== this._isOpen) {
      this._isOpen = value;
      this._syncOpenAttributes(value);
      if (this.isConnected) {
        this._animate(value);
      }
    }
  }

  onClickOutside(event) {
    if (!this.contains(event.target) && !(event.target.closest('details') instanceof DropdownDetails)) {
      this.isOpen = false;
    }
  }

  onEscKeyboard(event) {
    if (event.code === 'Escape' && this.isOpen) {
      this.isOpen = false;
    }
  }

  onFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.isOpen = false;
    }
  }

  onHover(event) {
    // Lichcards 25-09-2026: korte sluitvertraging, zodat het uitklapmenu niet dichtklapt als je
    // schuin met de muis naar het paneel beweegt. Een ander menu openen sluit dit menu meteen.
    cancelAnimationFrame(this._hoverRaf);
    clearTimeout(this._hoverCloseTimer);
    if (event.type === 'mouseenter') {
      this._hoverRaf = requestAnimationFrame(() => {
        const list = this.parentElement && this.parentElement.closest('ul');
        if (list) {
          list.querySelectorAll('details[open]').forEach((other) => {
            if (other !== this && typeof other.close === 'function') {
              clearTimeout(other._hoverCloseTimer);
              other.close();
            }
          });
        }
        this.isOpen = true;
      });
      return;
    }
    this._hoverCloseTimer = setTimeout(() => {
      this.isOpen = false;
    }, 260);
  }

  _toggleOpen(event) {
    if (event.target.closest('a[href]')) return;

    const href =
      event.target.closest('[data-href]')?.dataset?.href ||
      this._getSummaryParentHref();
    if (href && event.type === 'click') {
      window.location.assign(href);
      return;
    }
    event.preventDefault();
    this.isOpen = !this.isOpen;
  }

  close() {
    this.isOpen = false;
    this._animate(false);
  }

  _animate(open, preserveVisible = false) {
    if (!this.elements?.dropdown || !this.elements.dropdown.firstElementChild) return;

    this._syncOpenAttributes(open);

    if (open) {
      document.addEventListener('click', this.onClickOutsideListener);
      document.addEventListener('keydown', this.onEscKeyboardListener);
      document.addEventListener('focusout', this.onFocusOutListener);
      if (preserveVisible) {
        Object.assign(this.elements.dropdown.style, { opacity: '1', visibility: 'visible', overflow: 'visible' });
      } else {
        this.transitionIn();
      }
    } else {
      this.elements.dropdown.removeAttribute('open');
      document.removeEventListener('click', this.onClickOutsideListener);
      document.removeEventListener('keydown', this.onEscKeyboardListener);
      document.removeEventListener('focusout', this.onFocusOutListener);
      this.transitionOut()
      if (!this.isOpen) this.removeAttribute('open');
    }
  }

  transitionIn() {
    Motion.animate(this.elements.dropdown, { opacity: [0, 1], visibility: 'visible', overflow: 'visible' }, { duration: theme.config.motionReduced ? 0 : 0.3, easing: [.7, 0, .2, 1], delay: theme.config.motionReduced ? 0 : 0.2 });
    const translateY = '-20%';
    return Motion.animate(this.elements.dropdown.firstElementChild, { transform: [`translateY(${translateY})`, 'translateY(0)'] }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] }).finished;
  }

  transitionOut() {
    Motion.animate(this.elements.dropdown, { opacity: 0, visibility: 'hidden', overflow: 'hidden' }, { duration: theme.config.motionReduced ? 0 : 0.3, easing: [.7, 0, .2, 1] });
    const translateY = '-20%';
    return Motion.animate(this.elements.dropdown.firstElementChild, { transform: `translateY(${translateY})` }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] }).finished;
  }
}

if (!customElements.get('dropdown-details')) {
  customElements.define('dropdown-details', DropdownDetails, { extends: 'details' });
}

class MegaMenuDetails extends DropdownDetails {
  constructor() {
    super();
  }

  connectedCallback() {
    theme.initWhenVisible(() => {
      this.renderContent();
      this.init();
    });
  }

  renderContent() {
    const template = this.querySelector('template');
    if (template) {
      const templateContent = theme.renderTemplate(template);
      this.appendChild(templateContent);
    }
  }
}
if (!customElements.get('mega-menu-details')) customElements.define('mega-menu-details', MegaMenuDetails, { extends: 'details' });

(function() {
  function isSafari() {
    const userAgent = navigator.userAgent;
    return /^((?!chrome|android).)*safari/i.test(userAgent);
  }

  if (isSafari()) {
    function transitionIn(dropdown) {
      if (!dropdown || !dropdown.firstElementChild) return;
      Motion.animate(dropdown, { opacity: [0, 1], visibility: 'visible', overflow: 'visible' }, { duration: theme.config.motionReduced ? 0 : 0.3, easing: [.7, 0, .2, 1], delay: theme.config.motionReduced ? 0 : 0.2 });
      const translateY = '-20%';
      return Motion.animate(dropdown.firstElementChild, { transform: [`translateY(${translateY})`, 'translateY(0)'] }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] }).finished;
    }

    function transitionOut(dropdown) {
      if (!dropdown || !dropdown.firstElementChild) return;
      Motion.animate(dropdown, { opacity: 0, visibility: 'hidden', overflow: 'hidden' }, { duration: theme.config.motionReduced ? 0 : 0.3, easing: [.7, 0, .2, 1] });
      const translateY = '-20%';
      return Motion.animate(dropdown.firstElementChild, { transform: `translateY(${translateY})` }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] }).finished;
    }

    // Lichcards 25-09-2026: ook in Safari (dat de gewone hover-afhandeling niet draait)
    // sluit het menu pas na 260 ms, zodat het open blijft als je schuin naar het paneel
    // beweegt; een ander menu openen sluit het vorige direct.
    function setupSafariHoverFallback(details) {
      if (details.dataset.lcSafariHover) return;
      const summary = details.querySelector('summary');
      const dropdown = details.querySelector('.details__list');
      
      if (summary && dropdown) {
        details.dataset.lcSafariHover = 'true';
        const close = () => {
          clearTimeout(details._lcCloseTimer);
          if (!details._lcShown) return;
          details._lcShown = false;
          const transitionPromise = transitionOut(dropdown);
          if (transitionPromise && transitionPromise.then) {
            transitionPromise.then(() => {
              if (!details._lcShown) details.setAttribute('open', 'false');
            });
          } else {
            details.setAttribute('open', 'false');
          }
        };
        details._lcClose = close;
        summary.addEventListener('mouseenter', () => {
          clearTimeout(details._lcCloseTimer);
          const list = details.parentElement && details.parentElement.closest('ul');
          if (list) {
            list.querySelectorAll('details[data-lc-safari-hover]').forEach((other) => {
              if (other !== details && typeof other._lcClose === 'function') other._lcClose();
            });
          }
          if (details._lcShown) return;
          details._lcShown = true;
          details.setAttribute('open', 'true');
          transitionIn(dropdown);
        });
        details.addEventListener('mouseenter', () => {
          clearTimeout(details._lcCloseTimer);
        });
        details.addEventListener('mouseleave', () => {
          clearTimeout(details._lcCloseTimer);
          details._lcCloseTimer = setTimeout(close, 260);
        });
      }
    }

    function initSafariHoverFallback() {
      document.querySelectorAll('details[activate-event="hover"]').forEach(details => {
        setupSafariHoverFallback(details);
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initSafariHoverFallback);
    } else {
      initSafariHoverFallback();
    }

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            if (node.matches && node.matches('details[activate-event="hover"]')) {
              setupSafariHoverFallback(node);
            }
            const hoverDetails = node.querySelectorAll && node.querySelectorAll('details[activate-event="hover"]');
            if (hoverDetails) {
              hoverDetails.forEach(details => {
                setupSafariHoverFallback(details);
              });
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
})();

